import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import hasAdminAccessRights from "../../../../helpers/hasAdminAccessRights.ts";
import Cookies from "universal-cookie";
import Login from "../../../Structure/Login/Login.js";
import config from "config";
import queryString from "query-string";
import useInboxQueryParams from "../../../../hooks/useInboxQueryParams.ts";
import { filterPickerProjects } from "contexts/pickerContext/helpers.ts";
import { sortingFiles } from "../../../../utils/gridHelpers.js";
import { useContent } from "../../../../contexts/contentContext/contentContext.js";
import useInbox from "hooks/useInbox.ts";
import { useMail } from "../../../../contexts/mailContext/mailContext.js";
import { useModal } from "../../../../contexts/modalContext/modalContext.js";
import { usePicker } from "contexts/pickerContext/pickerContext.ts";
import { useUser } from "../../../../contexts/userContext/userContext.js";
import { useAppSelector } from "store/hooks.ts";
import {
  selectTParam,
  selectFParam,
  selectItemsPerPage,
  selectCurrentPage,
} from "store/selectors/inboxSelectors.ts";
import {
  selectPickerSearchParams,
  selectSearchParams,
} from "store/selectors/inboxSelectors.ts";

export const useInboxFetchLogic = () => {
  const { setModal } = useModal();
  const { getUserData, isLoggedIn } = useUser();

  const { getFilesData, setFilesList } = useMail();

  const {
    fetchInboxData,
    getSorting,
    getSearchParams,
    getMailsData,
    getIsFetching,
    getSelectedList,
    setSelectedList,
    setCurrentSelectFilter,
    setSplitView,
    setSelectedMail,
    setSorting,
    setSearchParams,
    setIsFetching,
  } = useInbox();
  const searchParams = useAppSelector(selectSearchParams);
  const pickerSearchParams = useAppSelector(selectPickerSearchParams);
  const pageType = useAppSelector(selectTParam);
  const pageCategory = useAppSelector(selectFParam);
  const itemsPerPage = useAppSelector(selectItemsPerPage);
  const currPageNum = useAppSelector(selectCurrentPage);
  const mailsData = getMailsData() || {};

  const { getIsFetchingPickerData, getPickerMailList } = usePicker();
  const { getTextByKey, getPath, getLanguage } = useContent();
  const { anotherParam } = useParams();
  const [fullScreenMessageData, setFullScreenMessageData] = useState(null);
  const firstLoadRef = useRef(true);
  const [
    isRecipientFilterDefaultValueSet,
    setIsRecipientFilterDefaultValueSet,
  ] = useState(false);
  const userData = getUserData() || {};

  // change sorting to size on manageStorage page
  useEffect(() => {
    if (window.location.href.includes("manageStorage")) {
      setSorting({ by: "size", asc: true });
    }
  }, []);

  useEffect(() => {
    if (Object.keys(userData).length && !userData.subUsersCount) {
      setSearchParams({
        su: [],
      });
    }
  }, [userData]);

  useEffect(() => {
    const showRecipientParam = hasAdminAccessRights(
      userData.isTopUser,
      userData.subUserRole
    );

    if (userData && !showRecipientParam) {
      setSearchParams({
        rcp: [],
      });
    }
  }, [userData]);

  useEffect(() => {
    // Set first mail selected on inbox mounted
    if (!firstLoadRef.current && !getIsFetching() && pageType !== "picker") {
      setSplitView(true);
      const firstMail = Object.values(mailsData)?.[0];

      if (firstMail) {
        setSelectedMail(firstMail);
      }
    }
  }, [firstLoadRef.current, getIsFetching()]);

  // INBOX+MAILDATA
  // logic to get all files from mail from inbox in lightbox
  useEffect(() => {
    const list = sortingFiles(
      getFilesData(),
      {},
      {},
      { by: "Name", asc: true }
    );
    setFilesList(list);
  }, [getFilesData()]);

  // navigate from old forms page to the new one
  useEffect(() => {
    if (getSearchParams()?.t === "forms") {
      window.open(
        `${getLanguage()}/inbox?t=received&f=jform`,
        "_self",
        "noopener,noreferrer"
      );
    }
  }, [getSearchParams()?.t]);

  // logic of fullscreenmessages for QA
  useEffect(() => {
    if (anotherParam) {
      if (anotherParam === "empty-response-message") {
        setFullScreenMessageData({
          message: {
            data: {
              title: getTextByKey("no-items-to-show-title"),
              content: getTextByKey("no-items-to-show-text"),
              image: { src: "inbox-empty", ext: "png" },
              containerCenter: true,
            },
            isShow: true,
          },
          isShowOnStart: true,
        });
      } else {
        setFullScreenMessageData(null);
      }
    }
  }, []);

  // Set default recipient filter for top users and sub users to "all"
  useEffect(() => {
    if (userData) {
      const showRecipientParam = hasAdminAccessRights(
        userData.isTopUser,
        userData.subUserRole
      );
      if (showRecipientParam && !searchParams?.rcp?.length) {
        if (searchParams?.su?.length) {
          setSearchParams({
            rcp: ["organization"],
          });
        } else {
          setSearchParams({
            rcp: ["my", "organization"],
          });
        }
      }
      setIsRecipientFilterDefaultValueSet(true);
    }
  }, [userData?.isTopUser, userData?.subUserRole, searchParams?.rcp?.length]);

  //logic of fetching in case chagning of filters/seraching/sorting
  useEffect(() => {
    if (userData.UserId && pageType && isRecipientFilterDefaultValueSet) {
      firstLoadRef.current = false;
      if (pageType === "picker" && Object.keys(mailsData)?.length) {
        setIsFetching(false);
      } else {
        fetchInboxData(userData.UserId);
      }
    }
    setSelectedList([]);
  }, [
    userData.UserId,
    pageType,
    pageCategory,
    currPageNum,
    searchParams?.q,
    searchParams?.sm?.join(","),
    searchParams?.sa?.join(","),
    searchParams?.sf?.join(","),
    searchParams?.rcp?.join(","),
    searchParams?.su?.join(","),
    searchParams?.pn,
    searchParams?.l?.join(","),
    getSorting().asc,
    getSorting().by,
    isRecipientFilterDefaultValueSet,
    itemsPerPage,
  ]);

  // when selected list is changing check if its clear and delete current selection filter
  useEffect(() => {
    if (getSelectedList()?.length < 1) {
      setCurrentSelectFilter("");
    }
  }, [getSelectedList()]);

  // logic of fullscreenMessages in case user is not logged in or no data within response
  useEffect(() => {
    const cookies = new Cookies();
    const isTokenExist = cookies.get(config.api.jumbomailApiAuthCookie);
    const dataLength =
      pageType === "picker"
        ? getPickerMailList()?.length
        : Object.keys(mailsData)?.length;
    //if user is not loged in show message and login-modal, if not...
    if (isLoggedIn() || isTokenExist) {
      // if user is loggedin but not authentificated do redirect to relevant page
      if (isLoggedIn() && !userData.IsAuthenticated) {
        window.location.href =
          config.baseurl + getPath() + "registration-complete/incomplete";
      }
      if (pageType === "picker") {
        if (
          !filterPickerProjects(
            getPickerMailList(),
            searchParams.q,
            pickerSearchParams.ps || []
          )?.length &&
          !getIsFetchingPickerData()
        ) {
          setFullScreenMessageData({
            message: {
              data: {
                title: getTextByKey("no-gallery-to-show-title"),
                content: getTextByKey("no-items-to-show-text"),
                image: { src: "inbox-empty", ext: "png" },
                containerCenter: true,
              },
              isShow: true,
            },
            isShowOnStart: true,
          });
        } else {
          setFullScreenMessageData(null);
        }
      } else {
        // ...if response dont have any items and fetching is finished show relevant message, otherwise remove fullscreen message
        if (dataLength > 0 || getIsFetching()) {
          setFullScreenMessageData(null);
        } else if (!getIsFetching()) {
          setFullScreenMessageData({
            message: {
              data: {
                title: getTextByKey("no-items-to-show-title"),
                content: getTextByKey("no-items-to-show-text"),
                image: { src: "inbox-empty", ext: "png" },
                containerCenter: true,
              },
              isShow: true,
            },
            isShowOnStart: true,
          });
        }
      }
    } else {
      setFullScreenMessageData({
        message: {
          data: {
            title: getTextByKey("no-login-user-title"),
            content: getTextByKey("no-login-user-text"),
            image: { src: "inbox-empty", ext: "png" },
            containerCenter: true,
          },
          isShow: true,
        },
        isShowOnStart: true,
      });
      setModal({
        modal: Login,
        modalSize: "full-modal",
      });
    }
  }, [
    isLoggedIn(),
    mailsData,
    getIsFetching(),
    pageType,
    getIsFetchingPickerData(),
    getPickerMailList()?.length,
  ]);

  //childer
  return fullScreenMessageData;
};
